import { useEffect, useState } from 'react';
import { 
  ViewportUtils, 
  TouchGestureUtils, 
  MobilePerformanceUtils,
  NetworkOptimizationUtils,
  BatteryOptimizationUtils,
  initializeMobileOptimizations 
} from '../utils/mobileOptimization';

/**
 * Hook for comprehensive mobile optimization
 */
export const useMobileOptimization = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<'slow' | 'fast' | 'unknown'>('unknown');
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);

  useEffect(() => {
    // Initialize all mobile optimizations
    initializeMobileOptimizations();

    // Set up viewport monitoring
    const checkViewport = () => {
      const { width } = ViewportUtils.getViewportSize();
      setIsMobile(width <= 768);
      setIsLandscape(ViewportUtils.isLandscape());
    };

    // Set up connection monitoring
    const checkConnection = () => {
      setConnectionQuality(NetworkOptimizationUtils.getConnectionQuality());
    };

    // Set up battery monitoring
    const checkBattery = async () => {
      if ('getBattery' in navigator) {
        try {
          const battery = await (navigator as any).getBattery();
          setBatteryLevel(battery.level);
          
          battery.addEventListener('levelchange', () => {
            setBatteryLevel(battery.level);
          });
        } catch (error) {
          console.log('Battery API not supported');
        }
      }
    };

    // Initial checks
    checkViewport();
    checkConnection();
    checkBattery();

    // Event listeners
    window.addEventListener('resize', checkViewport);
    window.addEventListener('orientationchange', () => {
      setTimeout(checkViewport, 100);
    });

    // Connection change listener
    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener('change', checkConnection);
    }

    return () => {
      window.removeEventListener('resize', checkViewport);
      window.removeEventListener('orientationchange', checkViewport);
      if (connection) {
        connection.removeEventListener('change', checkConnection);
      }
    };
  }, []);

  return {
    isMobile,
    isLandscape,
    connectionQuality,
    batteryLevel,
    isLowBattery: batteryLevel !== null && batteryLevel < 0.2,
    isSlowConnection: connectionQuality === 'slow'
  };
};

/**
 * Hook for mobile touch gestures
 */
export const useMobileGestures = (
  elementRef: React.RefObject<HTMLElement>,
  callbacks: {
    onSwipeLeft?: () => void;
    onSwipeRight?: () => void;
    onSwipeUp?: () => void;
    onSwipeDown?: () => void;
    onPullToRefresh?: () => Promise<void>;
  }
) => {
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const cleanupSwipe = TouchGestureUtils.addSwipeGesture(element, callbacks);
    
    let cleanupPullToRefresh: (() => void) | undefined;
    if (callbacks.onPullToRefresh) {
      cleanupPullToRefresh = TouchGestureUtils.addPullToRefresh(
        element,
        callbacks.onPullToRefresh
      );
    }

    return () => {
      cleanupSwipe();
      cleanupPullToRefresh?.();
    };
  }, [elementRef, callbacks]);
};

/**
 * Hook for mobile viewport management
 */
export const useMobileViewport = () => {
  const [viewportSize, setViewportSize] = useState(ViewportUtils.getViewportSize());
  const [safeAreaInsets, setSafeAreaInsets] = useState(ViewportUtils.getSafeAreaInsets());

  useEffect(() => {
    const updateViewport = () => {
      setViewportSize(ViewportUtils.getViewportSize());
      setSafeAreaInsets(ViewportUtils.getSafeAreaInsets());
    };

    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', () => {
      setTimeout(updateViewport, 100);
    });

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  return {
    viewportSize,
    safeAreaInsets,
    isPortrait: viewportSize.height > viewportSize.width,
    isLandscape: viewportSize.width > viewportSize.height
  };
};

/**
 * Hook for mobile performance monitoring
 */
export const useMobilePerformance = () => {
  const [isLowEndDevice, setIsLowEndDevice] = useState(false);
  const [memoryInfo, setMemoryInfo] = useState<any>(null);

  useEffect(() => {
    // Detect low-end devices
    const lowEnd = navigator.hardwareConcurrency <= 2 || 
                   (navigator as any).deviceMemory <= 2 ||
                   /Android.*Chrome\/[0-5]/.test(navigator.userAgent);
    
    setIsLowEndDevice(lowEnd);

    // Get memory info if available
    if ('memory' in performance) {
      setMemoryInfo((performance as any).memory);
    }

    // Apply performance optimizations
    if (lowEnd) {
      MobilePerformanceUtils.optimizeAnimations();
    }
  }, []);

  return {
    isLowEndDevice,
    memoryInfo,
    shouldReduceAnimations: isLowEndDevice
  };
};

/**
 * Hook for mobile network awareness
 */
export const useMobileNetwork = () => {
  const [connectionInfo, setConnectionInfo] = useState<any>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const connection = (navigator as any).connection || 
                     (navigator as any).mozConnection || 
                     (navigator as any).webkitConnection;

    if (connection) {
      setConnectionInfo({
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      });

      const handleConnectionChange = () => {
        setConnectionInfo({
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData
        });
      };

      connection.addEventListener('change', handleConnectionChange);

      return () => {
        connection.removeEventListener('change', handleConnectionChange);
      };
    }

    const handleOnlineChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineChange);
    window.addEventListener('offline', handleOnlineChange);

    return () => {
      window.removeEventListener('online', handleOnlineChange);
      window.removeEventListener('offline', handleOnlineChange);
    };
  }, []);

  return {
    connectionInfo,
    isOnline,
    isSlowConnection: connectionInfo?.effectiveType === '2g' || connectionInfo?.effectiveType === 'slow-2g',
    saveDataEnabled: connectionInfo?.saveData || false
  };
};